import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { StepChildProps } from 'sos-common/js/components/WizardStepper/WizardStepper.types';
import { makeTranslator, translateFunc } from '../../../utilities/translations';
import {
  ResearchOrderStore,
  researchOrderFiles,
  ResearchOrderRegisters,
} from '../../../models/ResearchOrderStore.types';
import { saveFormOrder } from 'sos-eservice-common/js/Redux/Actions';
import { ResearchOrderModel } from '../../../models/ResearchOrderModel';
import { ValidationResult, StepValidationMap } from 'sos-common/js/commonTypes/validation.types';
import SummaryStep from '../../../components/SummaryStep';
import SummaryStepFragment from '../../../components/SummaryStepFragment';
import _ from 'lodash';
import { autobind } from 'sos-common/js/utils/autobind';
import { getErrorMessageByFieldName, stringWithLineBreak } from 'sos-common/js/utils/helpers';
import { areAllFieldsErrorFree, getFileNameFromId } from '../../../utilities/helpers';
import { PrintButton } from 'sos-common/js/components/PrintButton/PrintButton';
import SendButton from 'sos-common/js/components/FormControls/SendButton';

export interface Step5State {
  isValid: boolean;
}
export interface Step5Props extends StepChildProps {
  Translate?: translateFunc;
  researchOrder: ResearchOrderModel;
  saveFormOrder?: typeof saveFormOrder;
  validationResults: ValidationResult[];
  stepValidationMap: StepValidationMap;
  files: researchOrderFiles;
  registers: ResearchOrderRegisters;
  isSending: boolean;
}

let ProtectedByPrivacyLawOptions = {
  Osl: '',
  Osf: '',
  Other: '',
};

class Granska extends React.Component<Step5Props, Step5State> {
  constructor(props: any) {
    super(props);

    ProtectedByPrivacyLawOptions.Osf = props.Translate('/orderFormPage/StatistikSekretess2');
    ProtectedByPrivacyLawOptions.Osl = props.Translate('/orderFormPage/StatistikSekretess');
    ProtectedByPrivacyLawOptions.Other = props.Translate('/orderFormPage/AnnatLagrum');

    this.state = {
      isValid: this.props.stepState.isDone,
    };
  }
  getTitle = () => 'Granska och skicka in';

  onClickSave = (e: React.MouseEvent) => {
    e.preventDefault();
    this.props.saveFormOrder();
  };

  componentDidMount() {
    this.props.reportToWizard({
      stepLabel: this.getTitle(),
    });
  }

  getFormValue = (formName: string, detailedFormName = null): string => {
    let value = _.get(this.props.researchOrder, formName);
    if (value === null || value === undefined || value === '' || value.length === 0) {
      value = this.props.Translate('/general/forms/uppSaknas');
      if (detailedFormName !== null) {
        value = detailedFormName + ' saknas';
      }
    }
    value = String(value); //don't delete this line :p
    switch (value) {
      case 'false':
        value = 'Nej';
        break;
      case 'true':
        value = 'Ja';
        break;
      case 'PNumber':
        value = 'Personnummer';
        break;
      case 'Number':
        value = 'Löpnummer';
        break;
      case 'NumberSaved':
        value = 'Löpnummer med sparad löpnummernyckel';
        break;
      case 'Osl':
        value = '11 kap 3 § OSL';
        break;
      case 'Osf':
        value = '7 § OSF';
        break;
      case 'Other':
        value = 'Annat lagrum';
        break;
    }
    return value;
  };

  @autobind
  validateFields(...fieldNames: string[]): boolean {
    const { validationResults } = this.props;
    return areAllFieldsErrorFree(validationResults, ...fieldNames);
  }

  @autobind
  getAllowedDataHandlers() {
    const handlers = this.props.researchOrder.Security.AllowedDataHandlers;

    let errorMessage = this.props.Translate('/orderFormpage/uppSaknas');
    if (handlers === null || handlers.length === 0) {
      return errorMessage;
    }

    return handlers.map((handler, i) => {
      let name = handler.Name || handler.Name !== '' ? handler.Name : errorMessage;
      let title = handler.Title || handler.Title !== '' ? handler.Title : errorMessage;

      return (
        <div key={`handlers_${name}_${i}`}>
          {name}, {title}
        </div>
      );
    });
  }

  @autobind
  getClarificationNames() {
    let arr: string[] = [];
    arr.push('InternalRegisters');
    for (let i = 0; i < 11; i++) {
      arr.push(`RegisterPeriodClarifications[${i}].FileInfo.FileId`);
      arr.push(`RegisterPeriodClarifications[${i}].FileInfo.Page`);
      arr.push(`RegisterPeriodClarifications[${i}].StartDate`);
      arr.push(`RegisterPeriodClarifications[${i}].EndDate`);
    }
    return arr;
  }

  @autobind
  getContactPersonFields() {
    return [
      'ContactPerson.Name',
      'ContactPerson.Organisation',
      'ContactPerson.Institution',
      'ContactPerson.Email',
      'ContactPerson.Phone',
      'ContactPerson.Address.StreetAddress',
      'ContactPerson.Address.ZipCode',
      'ContactPerson.Address.City',
    ];
  }

  @autobind
  getRepresentativeFields() {
    return [
      'Representative._Representative.Name',
      'Representative.WorkTitle',
      'Representative._Representative.Institution',
      'Representative._Representative.Email',
    ];
  }

  @autobind
  getInvoiceFields() {
    return [
      'BillingDetails.Organisation',
      'BillingDetails.OrganisationNumber',
      'BillingDetails.BillingReference',
      'BillingDetails.BillingAddress.StreetAddress',
      'BillingDetails.BillingAddress.ZipCode',
      'BillingDetails.BillingAddress.City',
    ];
  }

  @autobind
  getDeliveryDefinitionFields() {
    return [
      'DeliveryDefinition.HowToDeliverData',
      'DeliveryDefinition.EthicsDecisionKey.Page',
      'DeliveryDefinition.EthicsDecisionKey.FileId',
      'DeliveryDefinition.PurposeForSaveSerialNo',
      'DeliveryDefinition.IntentToContactCitizens',
      'DeliveryDefinition.MotivationPersonalDataNeeded',
    ];
  }

  @autobind
  getRegisterNameFromId(id: number): string {
    const { externalRegistries, qualityRegistries } = this.props.registers;
    const allRegistries = externalRegistries.concat(qualityRegistries);
    let item = allRegistries.find((r) => r.DhsId == id);
    if (item === null || item === undefined) return '';
    return item.Name;
  }

  @autobind
  getRegisterNamesFromArray(registers: any[]): string[] {
    return registers.map((reg) => this.getRegisterNameFromId(reg));
  }

  getPriorOrderRenderState = () => {
    const { Translate } = this.props;

    var response = {
      value: this.getFormValue('HasPriorOrders'),
      exists: this.getFormValue('HasPriorOrders') === 'Ja',
      referenceNumberOutput: `${Translate('/orderFormpage/Step5Diarienmr')} ${this.getFormValue(
        'ReferenceNumberPriorOrder',
      )}`,
      NyaArgangar: {
        exists: this.getFormValue('PdfOnlyPriorOrders.NyaArgangar') === 'Ja',
        output: `${Translate('/orderformpage/NyaArgangar_PriorOrders')}: ${Translate('/global/yes')}`,
      },
      NyaVariabler: {
        exists: this.getFormValue('PdfOnlyPriorOrders.NyaVariabler') === 'Ja',
        output: `${Translate('/orderformpage/NyaVariabler_PriorOrders')}: ${Translate('/global/yes')}`,
      },
      Annat: {
        exists: this.getFormValue('PdfOnlyPriorOrders.Annat') === 'Ja',
        output: `${Translate('/orderformpage/Annat_PriorOrders')}: ${Translate('/global/yes')}`,
        Vad: {
          titleOutput: Translate('/orderformpage/Vad_PriorOrders'),
          output:
            this.getFormValue('PdfOnlyPriorOrders.AngeVad').trim().length > 0
              ? this.getFormValue('PdfOnlyPriorOrders.AngeVad')
              : Translate('/orderformpage/KortfattadBeskrivningSaknas'),
        },
      },
    };
    return response;
  };

  public render() {
    const { Translate, researchOrder, files, validationResults, isSending } = this.props;
    const sendDisabled = validationResults && validationResults.length > 0 ? true : false;
    const isWhoCreatesPopulationHasValue =
      this.getFormValue('StudyPopulation.CreatedByUs') === 'Ja' ||
      this.getFormValue('StudyPopulation.CreatedByThem') === 'Ja' ||
      this.getFormValue('StudyPopulation.CreatedByOtherAuthority') === 'Ja' ||
      this.getFormValue('StudyPopulation.SavedKeyExist') === 'Ja';

    const hasDataSources =
      this.getFormValue('DataSources.ProprietaryData') === 'Ja' ||
      researchOrder.DataSources.QualityRegisters.length > 0 ||
      researchOrder.DataSources.ExternalRegisters.length > 0 ||
      researchOrder.DataSources.OtherRegisters[0] !== '';

    const stepOneFiles = files.ansokanEtik
      .concat(files.forskningsplan)
      .concat(files.ovrigtilletik)
      .concat(files.beslutetik)
      .concat(files.tillagansokanetik)
      .concat(files.tillagbeslutetik);

    const priorOrderRenderState = this.getPriorOrderRenderState();

    const errorMessageNarArProjektetSlutfort = getErrorMessageByFieldName(
      validationResults,
      'CurrentResearchOrder.Security.PdfOnly.NarArProjektetSlutfort',
    );

    return (
      <div id="step5" tabIndex={-1}>
        <h2>{Translate('/general/forms/summering')}</h2>

        <PrintButton />
        <div className="form-summary">
          {/************/}
          {/*  STEP 1  */}
          {/************/}
          <SummaryStep
            stepId={0}
            goToStep={this.props.navProps.goToStep}
            isDone={_.get(this.props.stepValidationMap, '[0].isValid', false)}
            headerTitle={Translate('/orderFormpage/projekt1')}
            headerBackString={Translate('/general/navigering/gaTillSteg1')}>
            {/*  Titel  */}
            <SummaryStepFragment isValid={this.validateFields('Title')}>
              <div>
                <strong>{Translate('/orderFormpage/titel')}</strong>
                <br />
                {this.getFormValue('Title')}
              </div>
            </SummaryStepFragment>

            {/*  Har du beställt data till detta projekt tidigare?  */}
            <SummaryStepFragment isValid={this.validateFields('HasPriorOrders', 'PdfOnly.AngeVad_PriorOrder')}>
              <div>
                <strong>{Translate('/orderFormpage/Step5Tidigarebest')}</strong>
                <br />
                {priorOrderRenderState.value}
                {priorOrderRenderState.exists && (
                  <>
                    <span>{priorOrderRenderState.referenceNumberOutput}</span>
                    <br />
                    {priorOrderRenderState.NyaArgangar.exists && (
                      <>
                        <span>{priorOrderRenderState.NyaArgangar.output}</span>
                        <br />
                      </>
                    )}
                    {priorOrderRenderState.NyaVariabler.exists && (
                      <>
                        <span>{priorOrderRenderState.NyaVariabler.output}</span>
                        <br />
                      </>
                    )}
                    {priorOrderRenderState.Annat.exists && (
                      <>
                        <span>
                          {priorOrderRenderState.Annat.output}
                          <br />
                          <strong>{priorOrderRenderState.Annat.Vad.titleOutput}</strong>
                          <br />
                          {priorOrderRenderState.Annat.Vad.output}
                        </span>
                        <br />
                      </>
                    )}
                  </>
                )}
              </div>
            </SummaryStepFragment>

            {/*  Är samtliga individer i studiepopulationen avlidna?  */}
            {/* Always valid since there's a default value */}
            <SummaryStepFragment isValid={true}>
              <div>
                <strong>{Translate('/orderFormpage/SamtAvl')}</strong>
                <br />
                {this.getFormValue('StudyPopulation.OnlyDeceased')}
              </div>
            </SummaryStepFragment>

            {/*  Bifogade filer:  */}
            <SummaryStepFragment isValid={this.validateFields('ansokanEtik', 'forskningsplan', 'beslutetik')}>
              <div>
                <strong>{Translate('/orderFormpage/BifFiler')}</strong>
                <br />
                {stepOneFiles.length == 0 && Translate('/general/forms/uppSaknas')}
                {stepOneFiles.map((file) => {
                  return (
                    <div
                      className="file-extension-icon form-summary__extension-icon"
                      key={`step1filenames_${file.FileId}`}>
                      {file.OriginalFilename}
                    </div>
                  );
                })}
              </div>
            </SummaryStepFragment>

            {/*  Urvalskriterier:  */}
            <SummaryStepFragment isValid={this.validateFields('StudyPopulation.ShouldSelectPartOfPopulation')}>
              <div>
                <strong>{Translate('/orderformpage/shouldSelectPartOfPopulation')}</strong>
                <br />
                {this.getFormValue('StudyPopulation.ShouldSelectPartOfPopulation')}
              </div>
            </SummaryStepFragment>

            {this.getFormValue('StudyPopulation.ShouldSelectPartOfPopulation') === 'Ja' && (
              <SummaryStepFragment isValid={this.validateFields('StudyPopulation.SelectionCriteria')}>
                <div>
                  <strong>{Translate('/orderformpage/selectionCriteria')}</strong>
                  <br />
                  <div className="form-summary__multiline-format">
                    {this.getFormValue('StudyPopulation.SelectionCriteria')}
                  </div>
                </div>
              </SummaryStepFragment>
            )}

            {/*  Studiepopulationen:  */}
            <SummaryStepFragment
              isValid={this.validateFields(
                'StudyPopulation.HowToCreatePopulation',
                'StudyPopulation.DescriptionInApplication.Page',
                'StudyPopulation.DescriptionInApplication.FileId',
              )}>
              <div>
                <strong>{Translate('/orderFormpage/studiepop')}</strong>
                <br />
                <div className="form-summary__multiline-format">
                  {this.getFormValue('StudyPopulation.HowToCreatePopulation')}
                </div>
                <strong>{Translate('/orderFormpage/Step5Beskrivs')}</strong>
                <br />
                {Translate('/orderFormpage/Step5Sida')}{' '}
                {this.getFormValue('StudyPopulation.DescriptionInApplication.Page')},{' '}
                {getFileNameFromId(this.getFormValue('StudyPopulation.DescriptionInApplication.FileId'), files)}
              </div>
            </SummaryStepFragment>

            {/*  Antal individer:  */}
            <SummaryStepFragment isValid={this.validateFields('StudyPopulation.EstimatedPopulationSize')}>
              <div>
                <strong>{Translate('/orderFormpage/antalIndv')}</strong>
                <br />
                {this.getFormValue('StudyPopulation.EstimatedPopulationSize')}
              </div>
            </SummaryStepFragment>

            {/*  Vem eller vilka behövs för att skapa populationen?  */}
            <SummaryStepFragment
              isValid={this.validateFields('StudyPopulation.CreatedByUs', 'StudyPopulation.SavedKeyLocation')}>
              <div>
                <strong>{Translate('/orderformpage/SkaparPopValidationHeading')}</strong>
                <br />
                {!isWhoCreatesPopulationHasValue && <div>{Translate('/orderformpage/uppSaknas')}</div>}
                {isWhoCreatesPopulationHasValue && (
                  <div>
                    {this.getFormValue('StudyPopulation.CreatedByUs') === 'Ja' && (
                      <div>{Translate('/orderformpage/Socialstyrelsen')}</div>
                    )}
                    {this.getFormValue('StudyPopulation.CreatedByThem') === 'Ja' && (
                      <div>{Translate('/orderformpage/EgenPopulation')}</div>
                    )}
                    {this.getFormValue('StudyPopulation.CreatedByOtherAuthority') === 'Ja' && (
                      <div>{Translate('/orderformpage/AndraRegisterHallare')}</div>
                    )}
                    {this.getFormValue('StudyPopulation.SavedKeyExist') === 'Ja' && (
                      <div>{Translate('/orderformpage/SparadNyckel')}</div>
                    )}

                    {this.getFormValue('StudyPopulation.SavedKeyExist') === 'Ja' && (
                      <div>
                        <strong>{Translate('/orderformpage/Step5Sparadnyckel')}</strong>
                        <br />
                        {this.getFormValue('StudyPopulation.SavedKeyLocation')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </SummaryStepFragment>

            {/*  Datakällor:  */}
            {/* Not obligatory but does not have any default value. Gray icon should be visible if empty. */}
            <SummaryStepFragment isValid={true} showNotObligatoryIcon={!hasDataSources}>
              <div>
                <strong>{Translate('/orderFormpage/Datakallor')}</strong>
                <br />
                {!hasDataSources && (
                  <div>
                    {Translate('/orderFormpage/uppSaknas')}. {Translate('/orderFormpage/NotRequired')}
                  </div>
                )}
                {hasDataSources && (
                  <div>
                    {this.getFormValue('DataSources.ProprietaryData') === 'Ja' && (
                      <div>{Translate('/orderFormpage/Step5EgenData')}</div>
                    )}
                    {this.props.researchOrder.DataSources.QualityRegisters.length > 0 && (
                      <div>
                        {this.getRegisterNamesFromArray(this.props.researchOrder.DataSources.QualityRegisters).map(
                          (registerName) => {
                            return <div key={registerName}>{registerName}</div>;
                          },
                        )}
                      </div>
                    )}
                    {this.props.researchOrder.DataSources.ExternalRegisters.length > 0 && (
                      <div>
                        {this.getRegisterNamesFromArray(this.props.researchOrder.DataSources.ExternalRegisters).map(
                          (registerName) => {
                            return <div key={registerName}>{registerName}</div>;
                          },
                        )}
                      </div>
                    )}
                    {this.props.researchOrder.DataSources.OtherRegisters.length > 0 && (
                      <div>
                        {this.props.researchOrder.DataSources.OtherRegisters.map((otherReg) => {
                          return <div key={otherReg}>{otherReg}</div>;
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </SummaryStepFragment>
          </SummaryStep>

          {/************/}
          {/*  STEP 2  */}
          {/************/}

          <SummaryStep
            stepId={1}
            goToStep={this.props.navProps.goToStep}
            isDone={_.get(this.props.stepValidationMap, '[1].isValid', false)}
            headerTitle={Translate('/orderFormpage/Register2')}
            headerBackString={Translate('/general/navigering/gaTillSteg2')}>
            <SummaryStepFragment isValid={this.validateFields(...this.getClarificationNames())}>
              <div className="container">
                <div className="d-none d-md-block">
                  <div className="row">
                    {researchOrder.RegisterPeriodClarifications.length > 0 && (
                      <React.Fragment>
                        <div className="col-md-4">
                          <strong>{Translate('/orderFormpage/register')}</strong>
                        </div>
                        <div className="col-md-3">
                          <strong>{Translate('/orderFormpage/Period')}</strong>
                        </div>
                        <div className="col-md-4">
                          <strong>{Translate('/orderFormpage/Step5Beskrivs')}</strong>
                        </div>
                      </React.Fragment>
                    )}
                    {researchOrder.RegisterPeriodClarifications.length === 0 && (
                      <div>
                        <strong>{Translate('/orderformpage/register')}</strong>
                        <br />
                        {Translate('/orderFormpage/uppSaknas')}
                      </div>
                    )}
                  </div>
                </div>
                {researchOrder.RegisterPeriodClarifications.map((register) => {
                  if (!register) return;
                  var startDate = register.StartDate;
                  var endDate = register.EndDate;
                  if (isNaN(startDate)) startDate = false;
                  if (isNaN(endDate)) endDate = false;

                  return (
                    <div key={'summary_step2_regs_' + register.Register} className="row">
                      <div className="col-md-4">
                        <strong className="d-md-none">{Translate('/orderFormpage/register')}</strong>
                        <div>{register.Register}</div>
                      </div>
                      <div className="col-md-3">
                        <strong className="d-md-none">{Translate('/orderFormpage/Period')}</strong>
                        {(!startDate || !endDate) && <div>{Translate('/orderformpage/uppSaknas')}</div>}
                        {startDate && endDate && <div>{`${startDate} - ${endDate}`}</div>}
                      </div>
                      <div className="col-md-4">
                        <strong className="d-md-none">{Translate('/orderFormpage/Step5Beskrivs')}</strong>
                        {!register.FileInfo && <div>{Translate('/orderformpage/uppSaknas')}</div>}
                        {register.FileInfo && (!register.FileInfo.FileId || !register.FileInfo.Page) && (
                          <div>{Translate('/orderformpage/uppSaknas')}</div>
                        )}
                        {register.FileInfo && register.FileInfo.FileId && register.FileInfo.Page && (
                          <div>
                            {Translate('/orderFormpage/Step5Sida')} {register.FileInfo.Page}
                            {', '}
                            {getFileNameFromId(register.FileInfo.FileId, files)}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </SummaryStepFragment>

            <SummaryStepFragment isValid={this.validateFields('variabellista')}>
              <div>
                <strong>{Translate('/orderFormpage/variabellista')}</strong>
                <br />
                {files.variabellista.length < 1 && Translate('/orderformpage/uppSaknas')}
                {files.variabellista.map((file) => {
                  return (
                    <div
                      className="file-extension-icon form-summary__extension-icon"
                      key={`step2filenames_${file.FileId}`}>
                      {file.OriginalFilename}
                    </div>
                  );
                })}
              </div>
            </SummaryStepFragment>
            <SummaryStepFragment isValid={true} showNotObligatoryIcon={files.kodlistaPatientRegistret.length === 0}>
              <div>
                <strong>{'Kodlista patientregistret'}</strong>
                <br />
                {files.kodlistaPatientRegistret.length === 0 &&
                  Translate('/orderformpage/uppSaknas') + '. ' + Translate('/orderFormpage/NotRequired')}
                {files.kodlistaPatientRegistret.map((file) => {
                  return (
                    <div
                      className="file-extension-icon form-summary__extention-icon"
                      key={`step2filenames_${file.FileId}`}>
                      {file.OriginalFilename}
                    </div>
                  );
                })}
              </div>
            </SummaryStepFragment>
            <SummaryStepFragment isValid={true} showNotObligatoryIcon={files.kodlistaLakemedelsRegistret.length === 0}>
              <div>
                <strong>{'Kodlista läkemedelsregistret'}</strong>
                <br />
                {files.kodlistaLakemedelsRegistret.length === 0 &&
                  Translate('/orderformpage/uppSaknas') + '. ' + Translate('/orderFormpage/NotRequired')}
                {files.kodlistaLakemedelsRegistret.map((file) => {
                  return (
                    <div
                      className="file-extension-icon form-summary__extention-icon"
                      key={`step2filenames_${file.FileId}`}>
                      {file.OriginalFilename}
                    </div>
                  );
                })}
              </div>
            </SummaryStepFragment>
            <SummaryStepFragment isValid={true} showNotObligatoryIcon={files.kodlistaDodsorsaksRegistret.length === 0}>
              <div>
                <strong>{'Kodlista dödsorsaksregistret'}</strong>
                <br />
                {files.kodlistaDodsorsaksRegistret.length === 0 &&
                  Translate('/orderformpage/uppSaknas') + '. ' + Translate('/orderFormpage/NotRequired')}
                {files.kodlistaDodsorsaksRegistret.map((file) => {
                  return (
                    <div
                      className="file-extension-icon form-summary__extention-icon"
                      key={`step2filenames_${file.FileId}`}>
                      {file.OriginalFilename}
                    </div>
                  );
                })}
              </div>
            </SummaryStepFragment>
          </SummaryStep>

          {/************/}
          {/*  STEP 3  */}
          {/************/}

          <SummaryStep
            stepId={2}
            goToStep={this.props.navProps.goToStep}
            isDone={_.get(this.props.stepValidationMap, '[2].isValid', false)}
            headerTitle={Translate('/orderFormpage/DataSkydd3')}
            headerBackString={Translate('/general/navigering/gaTillSteg3')}>
            <SummaryStepFragment isValid={this.validateFields(...this.getDeliveryDefinitionFields())}>
              <div>
                <strong>{Translate('/orderFormpage/DataLamnasMed')}</strong>
                <br />
                {this.getFormValue('DeliveryDefinition.HowToDeliverData')}
                <br />
                {/* Om man valt PERSONNUMMER */}
                {this.getFormValue('DeliveryDefinition.HowToDeliverData') === 'Med personnummer' && (
                  <div>
                    <strong>{Translate('/orderFormpage/Step5Beskrivs')}</strong>
                    <br />
                    {Translate('/orderFormpage/Step5Sida')}
                    {this.getFormValue('DeliveryDefinition.EthicsDecisionKey.Page')}, &nbsp;
                    {getFileNameFromId(this.getFormValue('DeliveryDefinition.EthicsDecisionKey.FileId'), files)}
                    <br />
                    <strong>{Translate('/orderFormpage/Step5Motivering')}</strong>
                    <br />
                    <span
                      dangerouslySetInnerHTML={{
                        __html: stringWithLineBreak(
                          this.getFormValue('DeliveryDefinition.MotivationPersonalDataNeeded'),
                        ),
                      }}></span>
                    <br />
                    <strong>{Translate('/orderFormpage/Step5AvserDuAtt')}</strong>
                    <br />
                    {this.getFormValue('DeliveryDefinition.IntentToContactCitizens')}
                  </div>
                )}

                {/* Om man valt LÖPNUMMER MED SPARAD NYCKEL */}
                {this.getFormValue('DeliveryDefinition.HowToDeliverData') ===
                  'Med löpnummer där en löpnummernyckel sparas' && (
                  <div>
                    <strong>{Translate('/orderFormpage/Step5Beskrivs')}</strong>
                    <br />
                    {Translate('/orderFormpage/Step5Sida')}
                    {this.getFormValue('DeliveryDefinition.EthicsDecisionKey.Page')}, &nbsp;
                    {getFileNameFromId(this.getFormValue('DeliveryDefinition.EthicsDecisionKey.FileId'), files)}
                    <br />
                    <strong>{Translate('/orderFormpage/Step5VarförLöpnummer')}</strong>
                    <br />
                    {this.getFormValue('DeliveryDefinition.PurposeForSaveSerialNo')
                      .split(',')
                      .map((purpose) => {
                        return <div key={purpose}>{purpose}</div>;
                      })}
                  </div>
                )}
              </div>
            </SummaryStepFragment>

            {/* Leveransformat */}
            <SummaryStepFragment isValid={this.validateFields('CurrentResearchOrder.FileFormatDeliveredData')}>
              <div>
                <strong>{Translate('/orderFormpage/Step5LeveransFormat')}</strong>
                <br />
                <span
                  dangerouslySetInnerHTML={{
                    __html: this.getFormValue('FileFormatDeliveredData').split(',').join('<br>'),
                  }}></span>
              </div>
            </SummaryStepFragment>

            <SummaryStepFragment
              isValid={this.validateFields(
                'Security.StatisticsPrivacyLaw',
                'CurrentResearchOrder.Security.ProtectedByPrivacyLaw',
                'CurrentResearchOrder.Security.PrivacyLaw',
                'CurrentResearchOrder.Security.OsfLaw',
                'CurrentResearchOrder.Security.OtherLaw',
                'Security.AllowedDataHandlers',
                'CurrentResearchOrder.Security.PdfOnly.NarArProjektetSlutfort',
              )}>
              <div>
                <strong>{Translate('/orderFormpage/SekretessBest')}</strong>
                <br />
                {this.getFormValue('Security.ProtectedByPrivacyLaw')}
                <br />
                {/* Kommer beställda data skyddas av sekretess enligt... 
                 Case: TRUE */}
                {this.getFormValue('Security.ProtectedByPrivacyLaw') === 'Ja' && (
                  <div>
                    <strong>{Translate('/orderFormpage/Step5Vilketlagrum')}</strong>

                    {this.getFormValue('Security.PrivacyLaw') !== ProtectedByPrivacyLawOptions.Other && (
                      <div>{this.getFormValue('Security.PrivacyLaw')}</div>
                    )}

                    {/* 7 § OSF */}
                    {this.getFormValue('Security.PrivacyLaw') === ProtectedByPrivacyLawOptions.Osf && (
                      <div>
                        <strong>{Translate('/orderFormPage/StatistikSekretess2Uppfylls')}</strong>
                        <div>{this.getFormValue('Security.OsfLaw')}</div>
                      </div>
                    )}

                    {/* Annat lagrum */}
                    {this.getFormValue('Security.PrivacyLaw') === ProtectedByPrivacyLawOptions.Other && (
                      <div>{this.getFormValue('Security.OtherLaw')}</div>
                    )}
                  </div>
                )}
                {/* Kommer beställda data skyddas av sekretess enligt... 
                 Case: FALSE */}
                {this.getFormValue('Security.ProtectedByPrivacyLaw') === 'Nej' && (
                  <div>
                    <strong>{Translate('/orderFormpage/Step5AllaPersoner')}</strong>
                    <br />
                    {this.getAllowedDataHandlers()}
                  </div>
                )}
                {this.getFormValue('Security.ProtectedByPrivacyLaw') === 'Nej' && (
                  <div>
                    <strong>{Translate('/orderFormPage/DatumForProjektAvslut')}</strong>
                    <br />
                    {errorMessageNarArProjektetSlutfort.length > 0
                      ? errorMessageNarArProjektetSlutfort
                      : this.getFormValue('Security.PdfOnly.NarArProjektetSlutfort')}
                  </div>
                )}
              </div>
            </SummaryStepFragment>
          </SummaryStep>

          {/************/}
          {/*  STEP 4  */}
          {/************/}

          <SummaryStep
            stepId={3}
            goToStep={this.props.navProps.goToStep}
            isDone={_.get(this.props.stepValidationMap, '[3].isValid', false)}
            headerTitle={Translate('/orderFormpage/Bestallare4')}
            headerBackString={Translate('/general/navigering/gaTillSteg4')}>
            <SummaryStepFragment isValid={this.validateFields('ResearchOrganisation')}>
              <div>
                <strong>{Translate('/orderFormpage/ForskHuvudman')}</strong>
                <br />
                {this.getFormValue('ResearchOrganisation')}
              </div>
            </SummaryStepFragment>
            <SummaryStepFragment isValid={this.validateFields(...this.getContactPersonFields())}>
              <div>
                <strong>{Translate('/orderFormpage/AnsvForsk')}</strong>
                <br />
                {this.getFormValue('ContactPerson.Name', 'Namn')}
                <br />
                {this.getFormValue('ContactPerson.Organisation', 'Organisation')}
                <br />
                {this.getFormValue('ContactPerson.Institution', 'Institution/avdelning/enhet')}
                <br />
                {this.getFormValue('ContactPerson.Email', 'E-postadress')}
                <br />
                {this.getFormValue('ContactPerson.Phone', 'Telefon')}
                <br />
                {this.getFormValue('ContactPerson.Address.StreetAddress', 'Postadress')}
                <br />
                {this.getFormValue('ContactPerson.Address.ZipCode', 'Postnummer')}
                <br />
                {this.getFormValue('ContactPerson.Address.City', 'Ort')}
              </div>
            </SummaryStepFragment>
            <SummaryStepFragment isValid={this.validateFields(...this.getRepresentativeFields())}>
              <div>
                <strong>{Translate('/orderFormpage/BehorFore')}</strong>
                <br />
                {this.getFormValue('Representative._Representative.Name', 'Namn')}
                <br />
                {this.getFormValue('Representative.WorkTitle', 'Titel')}
                <br />
                {this.getFormValue('Representative._Representative.Institution', 'Institution/avdelning/enhet')}
                <br />
                {this.getFormValue('Representative._Representative.Email', 'E-postadress')}
              </div>
            </SummaryStepFragment>
            <SummaryStepFragment isValid={this.validateFields(...this.getInvoiceFields())}>
              <div>
                <strong>{Translate('/orderFormpage/FaktMott')}</strong>
                <br />
                {this.getFormValue('BillingDetails.Organisation', 'Organisation')}
                <br />
                {this.getFormValue('BillingDetails.OrganisationNumber', 'Organisationsnummer')}
                <br />
                {this.getFormValue('BillingDetails.BillingReference', 'Fakturareferens')}
                <br />
                {this.getFormValue('BillingDetails.BillingAddress.StreetAddress', 'Fakturaadress (ej obligatorisk)')}
                <br />
                {this.getFormValue('BillingDetails.BillingAddress.ZipCode', 'Postnummer')}
                <br />
                {this.getFormValue('BillingDetails.BillingAddress.City', 'Ort')}
              </div>
            </SummaryStepFragment>
          </SummaryStep>
        </div>

        <div className="form-submit">
          <SendButton onClickSave={this.onClickSave} isSending={isSending} sendDisabled={sendDisabled} />
        </div>
      </div>
    );
  }
}

const mapState2Props = (state: ResearchOrderStore) => {
  return {
    Translate: makeTranslator(state.globals.translations),
    registers: state.registers,
    researchOrder: state.formModel,
    validationResults: state.validationState.validationResults,
    stepValidationMap: state.validationState.stepValidationMap,
    files: state.files,
    isSending: state.globals.isSending,
  } as Partial<Step5Props>;
};

const mapDispatch2Props = (dispatch) => {
  return {
    saveFormOrder: bindActionCreators(saveFormOrder, dispatch),
  } as Partial<Step5Props>;
};

const StepFive = connect(mapState2Props, mapDispatch2Props)(Granska);
export { StepFive };
